import { useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { Circle, MapContainer, TileLayer, useMap } from 'react-leaflet'
import { Navigation, SlidersHorizontal } from 'lucide-react'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import type { LatLngExpression } from 'leaflet'
import { Button } from '@/components/ui/button'
import { useTheme } from '@/lib/theme'
import { ServiceZoneLayer } from '@/components/service-zone-layer'

// Component to handle map centering
function MapCenter({ center }: { center: LatLngExpression }) {
  const map = useMap()
  useEffect(() => {
    map.setView(center, map.getZoom())
  }, [center, map])
  return null
}

// Component to handle map clicks for moving center
function MapClickHandler({
  onMapClick,
}: {
  onMapClick: (center: LatLngExpression) => void
}) {
  const map = useMap()

  useEffect(() => {
    const handleClick = (e: L.LeafletMouseEvent) => {
      onMapClick([e.latlng.lat, e.latlng.lng])
    }

    map.on('click', handleClick)

    return () => {
      map.off('click', handleClick)
    }
  }, [map, onMapClick])

  return null
}

// Draggable center marker component
function DraggableCenterMarker({
  center,
  onCenterChange,
}: {
  center: LatLngExpression
  onCenterChange: (center: LatLngExpression) => void
}) {
  const [position, setPosition] = useState(center)
  const map = useMap()
  const markerRef = React.useRef<L.Marker | null>(null)

  useEffect(() => {
    setPosition(center)
  }, [center])

  useEffect(() => {
    // Clean up existing marker
    if (markerRef.current) {
      try {
        markerRef.current.off('dragend')
        if (map.hasLayer(markerRef.current)) {
          map.removeLayer(markerRef.current)
        }
      } catch (e) {
        // Ignore cleanup errors
      }
      markerRef.current = null
    }

    const posArray = Array.isArray(position)
      ? position
      : [position.lat, position.lng]
    const marker = L.marker([posArray[0], posArray[1]], {
      draggable: true,
      icon: L.divIcon({
        html: '<div class="search-center-marker"></div>',
        className: 'draggable-center-marker',
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      }),
    })

    marker.addTo(map)
    markerRef.current = marker

    const handleDragEnd = (e: L.DragEndEvent) => {
      const newPos = e.target.getLatLng()
      const newCenter: LatLngExpression = [newPos.lat, newPos.lng]
      setPosition(newCenter)
      onCenterChange(newCenter)
    }

    marker.on('dragend', handleDragEnd)

    return () => {
      if (markerRef.current) {
        try {
          markerRef.current.off('dragend', handleDragEnd)
          if (map.hasLayer(markerRef.current)) {
            map.removeLayer(markerRef.current)
          }
        } catch (e) {
          // Ignore cleanup errors
        }
        markerRef.current = null
      }
    }
  }, [map, position, onCenterChange])

  return null
}

export interface MapSectionProps {
  searchCenter: LatLngExpression
  searchRadius: number
  onCenterChange: (center: LatLngExpression) => void
  onGoToUserLocation: () => void
  onToggleFilters: () => void
}

export function MapSection({
  searchCenter,
  searchRadius,
  onCenterChange,
  onGoToUserLocation,
  onToggleFilters,
}: MapSectionProps) {
  const { resolvedTheme } = useTheme()

  const tileLayerConfig = useMemo(() => {
    if (resolvedTheme === 'dark') {
      return {
        url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      }
    }
    return {
      url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    }
  }, [resolvedTheme])

  return (
    <div className="flex-1 relative">
      <MapContainer
        center={searchCenter}
        zoom={13}
        style={{ height: '100%', width: '100%', zIndex: 0 }}
        scrollWheelZoom={true}
      >
        <TileLayer
          key={resolvedTheme}
          attribution={tileLayerConfig.attribution}
          url={tileLayerConfig.url}
        />
        <MapCenter center={searchCenter} />
        <MapClickHandler onMapClick={onCenterChange} />
        <ServiceZoneLayer />
        <Circle
          center={searchCenter}
          radius={searchRadius}
          pathOptions={{
            color: '#3b82f6',
            fillColor: '#3b82f6',
            fillOpacity: 0.2,
            weight: 2,
          }}
        />
        <DraggableCenterMarker
          center={searchCenter}
          onCenterChange={onCenterChange}
        />
      </MapContainer>

      {/* Control Buttons */}
      <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
        <Button
          onClick={onGoToUserLocation}
          size="icon"
          className="h-10 w-10 rounded-full shadow-lg bg-background hover:bg-accent"
          variant="outline"
          aria-label="Go to my location"
        >
          <Navigation className="h-4 w-4" />
        </Button>
        <Button
          onClick={onToggleFilters}
          size="icon"
          className="h-10 w-10 rounded-full shadow-lg bg-background hover:bg-accent"
          variant="outline"
          aria-label="Toggle filters"
        >
          <SlidersHorizontal className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
